<template>
  <v-card flat height="90vh" class="overflow-hidden">
    <v-row no-gutters height="85vh" class="overflow-hidden">
      <v-col cols="3" style="border-right: 2px solid #e0e0e0">
        <v-card flat height="85vh" class="overflow-y-auto">
          <v-list v-model:opened="open" bg-color="transparent">
            <v-list-item
              :class="
                viewing === 'fleet-vehicles'
                  ? 'bg-primary40 text-pwt_black'
                  : 'bg-pwt_alabaster text-pwt_black'
              "
              class="text-caption"
              value="fleet-vehicles"
              @click="() => (viewing = 'fleet-vehicles')"
            >
              Fleet vehicles
            </v-list-item>
            <v-list-group value="all-vehicles" fluid>
              <template v-slot:activator="{ props }">
                <v-list-item
                  v-bind="props"
                  :class="
                    viewing === 'all-vehicles'
                      ? 'bg-primary40'
                      : 'bg-transparent'
                  "
                  @click="() => (viewing = 'all-vehicles')"
                >
                  <v-list-item-title class="text-caption">
                    All vehicles models
                  </v-list-item-title>
                </v-list-item>
              </template>

              <v-list
                v-for="makeList in makeLists"
                :key="makeList.letter"
                bg-color="transparent"
                class="pl-5"
                density="compact"
              >
                <v-list-item class="font-weight-bold">
                  {{ makeList.letter.toUpperCase() }}
                </v-list-item>
                <v-list-item
                  v-for="(make, i) in makeList.makes"
                  :key="makeList.letter + i"
                  class="text-caption"
                  @click="() => (searchQuery = make)"
                >
                  {{ make }}
                </v-list-item>
              </v-list>
            </v-list-group>
          </v-list>
        </v-card>
      </v-col>
      <v-col cols="9">
        <v-card flat height="85vh" class="overflow-y-auto">
          <v-text-field
            v-model="searchQuery"
            clearable
            hide-details
            density="compact"
            class="mx-5 mb-3 mt-5"
            bg-color="pwt_alabaster"
            placeholder="Search vehicles..."
          />

          <v-card-title v-if="viewing === 'fleet-vehicles'">
            Custom vehicles
          </v-card-title>
          <v-card-text v-if="viewing === 'fleet-vehicles'">
            <v-row v-if="customVehicles.length">
              <VehicleGalleryCard
                v-for="(vehicle, index) in customVehicles"
                :vehicle="vehicle"
                :is-selected="vehicleStore.defaultVehicle?.id === vehicle.id"
                :key="`vehicle-card-${index}`"
                @select="emitSelectedVehicle"
                @showDetails="emitSelectedVehicle"
              />
            </v-row>
            <span v-else> No custom vehicles </span>
          </v-card-text>

          <v-card-title v-if="viewing === 'fleet-vehicles'">
            Connected vehicles
          </v-card-title>
          <v-card-text v-if="viewing === 'fleet-vehicles'">
            <v-row v-if="connectedVehicles.length">
              <VehicleGalleryCard
                v-for="(vehicle, index) in connectedVehicles"
                :vehicle="vehicle"
                :is-selected="vehicleStore.defaultVehicle?.id === vehicle.id"
                :key="`vehicle-card-${index}`"
                @select="emitSelectedVehicle"
                @showDetails="emitSelectedVehicle"
              />
            </v-row>
            <span v-else> No connected vehicles </span>
          </v-card-text>

          <v-card-text v-if="viewing === 'all-vehicles'">
            <v-row>
              <VehicleGalleryCard
                v-for="(vehicle, index) in genericVehicles"
                :vehicle="vehicle"
                :is-selected="vehicleStore.defaultVehicle?.id === vehicle.id"
                :key="`generic-vehicle-card-${index}`"
                @select="emitSelectedVehicle"
                @showDetails="emitSelectedVehicle"
              />
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<script setup lang="ts">
import VehicleGalleryCard from "@/components/vehicles/VehicleGalleryCard.vue";
import { useVehicleStore, type Vehicle } from "@/domains/vehicles";
import { computed, ref } from "vue";

const vehicleStore = useVehicleStore();

const searchQuery = ref("");
const open = ref([]);
const viewing = ref<"fleet-vehicles" | "all-vehicles">("fleet-vehicles");
const models = computed<{ make: string }[]>(() => []); // TODO: find out what is happening with models in this POC, no evs are supported so this is just a placeholder for now
const makeLists = computed<{ letter: string; makes: string[] }[]>(() => {
  const tempArray: { letter: string; makes: string[] }[] = [];
  models.value.forEach((model) => {
    const make = model.make;
    const makeList = tempArray.find((m) => m.letter === make.toLowerCase()[0]);
    if (makeList) {
      if (!makeList.makes.includes(make)) makeList.makes.push(make);
    } else {
      tempArray.push({ letter: make.toLowerCase()[0]!, makes: [make] });
    }
  });
  // sort tempArray by letter
  tempArray.sort((a, b) => a.letter.localeCompare(b.letter));
  return tempArray;
});

const connectedVehicles = computed(() => {
  const unfilteredData: Vehicle[] = vehicleStore.getConnectedVehicles;
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    return unfilteredData.filter((vehicle) => {
      if (vehicle.name && vehicle.name.toLowerCase().includes(query))
        return vehicle;
      if (vehicle.VIN && vehicle.VIN.toLowerCase().includes(query))
        return vehicle;
      if (
        vehicle.licensePlate &&
        vehicle.licensePlate.toLowerCase().includes(query)
      )
        return vehicle;
    });
  }
  return unfilteredData;
});

const genericVehicles = computed(() => {
  const unfilteredData: Vehicle[] = vehicleStore.getGenericVehicles;
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    return unfilteredData.filter((vehicle) => {
      if (vehicle.name && vehicle.name.toLowerCase().includes(query))
        return vehicle;
      if (vehicle.VIN && vehicle.VIN.toLowerCase().includes(query))
        return vehicle;
      if (
        vehicle.licensePlate &&
        vehicle.licensePlate.toLowerCase().includes(query)
      )
        return vehicle;
      // if (vehicle.evModel) {
      //   if (vehicle.evModel.name.toLowerCase().includes(query)) return vehicle
      //   if (vehicle.evModel.make.toLowerCase().includes(query)) return vehicle
      // }
    });
  }
  return unfilteredData;
});

const customVehicles = computed(() => {
  const unfilteredData: Vehicle[] = vehicleStore.getCustomVehicles;
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    return unfilteredData.filter((vehicle) => {
      if (vehicle.name && vehicle.name.toLowerCase().includes(query))
        return vehicle;
      if (vehicle.VIN && vehicle.VIN.toLowerCase().includes(query))
        return vehicle;
      if (
        vehicle.licensePlate &&
        vehicle.licensePlate.toLowerCase().includes(query)
      )
        return vehicle;
      // if (vehicle.evModel) {
      //   if (vehicle.evModel.name.toLowerCase().includes(query)) return vehicle
      //   if (vehicle.evModel.make.toLowerCase().includes(query)) return vehicle
      // }
    });
  }
  return unfilteredData;
});

const emit = defineEmits<{
  (e: "vehicle-select", vehicle: Vehicle): void;
}>();

function emitSelectedVehicle(vehicle: Vehicle) {
  emit("vehicle-select", vehicle);
}
</script>
<style scoped>
* :deep(.v-list-item--active > .v-list-item__overlay) {
  opacity: 0 !important;
}
</style>
